/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { PreviewType } from 'client-code/previews';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import {
    PREVIEW_DESCRIPTION_ONE_LINE_LENGTH,
    PREVIEW_TITLE_MAX_LENGTH,
    PREVIEW_TITLE_ONE_LINE_LENGTH,
    PREVIEW_TITLE_SIZE_REDUCTION_LENGTH,
    THREE_LINES_PREVIEW_DESCRIPTION,
    TWO_LINES_PREVIEW_DESCRIPTION,
} from '../../../config';
import { AppUtils } from '../../../core/AppUtils';
import { ServicesClientImpl } from '../../../core/impls/ServicesClientImpl';
import { TOfferPreviewCustomData } from '../../../core/types/TOfferPreviewCustomData';
import { DeviceModel } from '../../../core/device/DeviceModel';

export class OfferPreviewFormatter {
    private services = new ServicesClientImpl();

    private EMPTY_SPACE = '\u2009';

    private LEFT_QUOTE = '\u2018';

    private RIGHT_QUOTE = '\u2019';

    private LONG_DASH = '\u2013';

    private mOfferPreview: TOfferPreviewCustomData = {};

    private mContentPreview: any = {};

    private mCcVideoResolutionText: string = '';

    private mPreviewTitleText: string = '';

    private mPreviewDescriptionText: string = '';

    private mCategoryAndFirstAiredText: string = '';

    private mInternalRatings: [] = [];

    constructor(offerPreview: TOfferPreviewCustomData) {
        this.mOfferPreview = offerPreview;
        this.mContentPreview = offerPreview.contentPreview;
        this._formatPreviewTitle();
        this._formatPreviewDescription();
        this._formatCcVideoResolution();
        this._formatCategoryAndFirstAired();
        this._storeInternalRatings();
    }

    private _formatPreviewTitle = (): void => {
        if (this.mContentPreview?.isAdult)
            this.mPreviewTitleText = Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
        else this.mPreviewTitleText = this.mContentPreview?.title || '';
    };

    public getPreviewTitle = (): string => {
        return this.mPreviewTitleText;
    };

    public hasMoviePreview = (): boolean => {
        const previewType: string = this.mContentPreview?.previewType;
        return previewType === PreviewType.MoviePreview;
    };

    public getMovieYear = (): string => {
        return this.mContentPreview?.isAdult ? '' : `(${this.mContentPreview.movieYear})`;
    };

    public hasPreviewTitleTruncation = (): boolean => {
        return this.mPreviewTitleText.length > PREVIEW_TITLE_MAX_LENGTH;
    };

    public hasNewIcon = (): boolean => {
        if (this.mContentPreview?.isAdult) return false;
        return this.mContentPreview?.isNew;
    };

    public getPreviewTitleClasses = (): string => {
        return `preview-title${
            this.mPreviewTitleText.length > PREVIEW_TITLE_SIZE_REDUCTION_LENGTH
                ? ' adjust-font'
                : ''
        }`;
    };

    private _formatPreviewDescription = (): void => {
        const seriesText = AppUtils.generateSeasonEpisodeText(
            this.mContentPreview?.seasonNumber,
            this.mContentPreview?.episodeNumber,
        );
        const episodeTitleText =
            (this.mContentPreview?.episodeTitle &&
                ` ${this.LEFT_QUOTE}${this.mContentPreview?.episodeTitle}${this.RIGHT_QUOTE} ${this.LONG_DASH} `) ||
            '';
        const description = this.mContentPreview?.description || '';

        this.mPreviewDescriptionText = `${seriesText}${episodeTitleText}${description}`;
    };

    private _formatCcVideoResolution = (): void => {
        const ccText = this.mOfferPreview.hasCc ? 'CC' : '';
        const videoResolutionText = this.mOfferPreview.videoResolution?.toUpperCase() || '';
        const ccVideoResolutionText = [ccText, videoResolutionText]
            .filter((value) => value.length > 0)
            .join(', ');

        this.mCcVideoResolutionText =
            ccVideoResolutionText.length > 0 ? `${this.EMPTY_SPACE}(${ccVideoResolutionText})` : '';
    };

    private _formatCategoryAndFirstAired = (): void => {
        const categoryText = this.mContentPreview?.categoryLabel
            ? this.mContentPreview.categoryLabel.split(',').splice(0, 2).join(',')
            : '';
        const firstAiredDate = this.mContentPreview?.firstAiredDate
            ? new Date(this.mContentPreview.firstAiredDate)
            : null;

        const currentTime = new Date();
        const isFutureAiring = firstAiredDate && firstAiredDate.getTime() > currentTime.getTime();

        const firstAiredText = isFutureAiring
            ? Locale.getString(LocaleKeys.FIRST_AIRS)
            : Locale.getString(LocaleKeys.FIRST_AIRED);

        const firstAiredFormattedText = firstAiredDate
            ? AppUtils.strftime(firstAiredDate, `${firstAiredText} %M/%D/%y`)
            : '';
        this.mCategoryAndFirstAiredText = [categoryText, firstAiredFormattedText]
            .filter((value) => value.length > 0)
            .join(', ');
    };

    public getPreviewDescription = (): string => {
        return this.mContentPreview?.isAdult
            ? Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN)
            : this.mPreviewDescriptionText;
    };

    public getCcVideoResolution = (): string => {
        return this.mContentPreview?.isAdult ? '' : this.mCcVideoResolutionText;
    };

    public getCategoryAndFirstAired = (): string => {
        return this.mContentPreview?.isAdult
            ? ''
            : `${this.EMPTY_SPACE}${this.mCategoryAndFirstAiredText}`;
    };

    public hasPreviewDescriptionTruncation = (): boolean => {
        const numberOfDiscriptionLines =
            this.getPreviewTitle().length > PREVIEW_TITLE_ONE_LINE_LENGTH
                ? TWO_LINES_PREVIEW_DESCRIPTION
                : THREE_LINES_PREVIEW_DESCRIPTION;
        const descriptionMaxLength = PREVIEW_DESCRIPTION_ONE_LINE_LENGTH * numberOfDiscriptionLines;
        const descriptionLength =
            this.mPreviewDescriptionText.length +
            this.mCcVideoResolutionText.length +
            this.mCategoryAndFirstAiredText.length;

        return descriptionLength > descriptionMaxLength;
    };

    public getPreviewDescriptionClasses = (): string => {
        return `preview-description${
            this.getPreviewTitle().length > PREVIEW_DESCRIPTION_ONE_LINE_LENGTH
                ? ' reduce-height'
                : ''
        }`;
    };

    private _storeInternalRatings = (): void => {
        if (!this.mContentPreview?.isAdult)
            this.mInternalRatings =
                this.mContentPreview?.internalRatingList
                    ?.map(({ typeId, valueId }) => {
                        return this.services.getInternalRatingName(`${typeId}${valueId}`);
                    })
                    .filter((ratingName: string) => ratingName.length > 0) || [];
    };

    public getInternalRatings = (): [] => {
        return this.mInternalRatings;
    };

    public getContentAiringDateTime = (): string => {
        return !this.mContentPreview?.isAdult
            ? AppUtils.generateDateTimeText(
                  this.mContentPreview?.startTime,
                  this.mContentPreview?.endTime,
                  this.mContentPreview?.duration,
                  DeviceModel.getTVProperties().getLang(),
              )
            : '';
    };

    public hasStarRating = (): boolean => {
        return !this.mContentPreview?.isAdult
            ? this.mContentPreview?.starRating !== undefined
            : false;
    };

    public getStarRating = (): number => {
        return this.mContentPreview?.starRating || 0;
    };

    public getStreamType = (): string => {
        return this.mContentPreview?.streamType || '';
    };

    public isAvailableInSocu = (): boolean => {
        return !!this.mOfferPreview?.isAvailableInSocu;
    };

    public getProvidersImageUrls = (): string[] | undefined => {
        return this.mOfferPreview?.contentPreview?.providersImageUrls;
    };
}
